import React, { ReactElement } from "react";

import styled from "styled-components";

/**
 * FeatureAvailability — a compact, data-driven block that states where a
 * feature is available: on which hosting, on which plans and to which roles.
 *
 * It is the reusable version of the availability panel used in the Help Docs
 * example articles, extracted so several stories (and, later, the real
 * PortableText renderer) can share one implementation and one visual language:
 *
 *   • green check   → available
 *   • analog clock  → decided but not shipped yet ("to be determined")
 *   • strikethrough → not included
 *
 * The component is theme-agnostic (all colors come from `--color-*` tokens) and
 * self-contained (it carries its own typography, so it renders correctly
 * outside the article body as well).
 */

export type AvailabilityStatus = "available" | "pending" | "excluded";

export interface AvailabilityMark {
  label: string;
  status: AvailabilityStatus;
  /** Muted parenthetical, e.g. "(stable, since Jun 17 2026)". Include the parens. */
  note?: string;
  /** Italic muted trailing text for the pending state, e.g. "to be determined". */
  pending?: string;
  /**
   * Nested marks rendered in parentheses, e.g. Member+ (PM ✓ / FM ✓). When a
   * mark has children it does not render its own status icon — the children
   * carry the marks.
   */
  children?: AvailabilityMark[];
}

export interface AvailabilityRow {
  label: string;
  items: AvailabilityMark[];
  /** Icon before the label (Availability row) or after it (Plans / Roles). Default "after". */
  iconPosition?: "before" | "after";
  /** Separate items with a middot (used on the Availability row). Default false. */
  separated?: boolean;
}

export interface FeatureAvailabilityProps {
  rows: AvailabilityRow[];
  className?: string;
}

/* ------------------------------------------------------------------ */
/* Marks                                                               */
/* ------------------------------------------------------------------ */

const CheckMark = (): ReactElement => (
  <svg
    className="ap-ic ap-yes"
    viewBox="0 0 16 16"
    width="15"
    height="15"
    aria-hidden
  >
    <path
      d="M13 4.6 L6.5 11.4 L3 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockMark = (): ReactElement => (
  <svg
    className="ap-ic ap-clock"
    viewBox="0 0 16 16"
    width="14"
    height="14"
    aria-hidden
  >
    <circle
      cx="8"
      cy="8"
      r="6.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M8 8 L8 4.4 M8 8 L10.5 9.3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

const StatusIcon = ({
  status,
}: {
  status: AvailabilityStatus;
}): ReactElement | null => {
  if (status === "available") {
    return <CheckMark />;
  }
  if (status === "pending") {
    return <ClockMark />;
  }
  return null;
};

/** A nested mark inside parentheses, e.g. "PM ✓". */
const ChildMark = ({ mark }: { mark: AvailabilityMark }): ReactElement =>
  mark.status === "excluded" ? (
    <span className="ap-excluded">{mark.label}</span>
  ) : (
    <>
      {mark.label} <StatusIcon status={mark.status} />
    </>
  );

const Mark = ({
  mark,
  iconPosition,
}: {
  mark: AvailabilityMark;
  iconPosition: "before" | "after";
}): ReactElement => {
  const hasChildren = Boolean(mark.children && mark.children.length > 0);
  const excluded = mark.status === "excluded";
  const icon = hasChildren ? null : <StatusIcon status={mark.status} />;

  return (
    <span className={excluded ? "ap-item ap-excluded" : "ap-item"}>
      {iconPosition === "before" && icon}
      {mark.label}
      {mark.note && <span className="ap-sub">{mark.note}</span>}
      {mark.pending && <span className="ap-tbd">{mark.pending}</span>}
      {hasChildren && (
        <span className="ap-sub">
          (
          {mark.children?.map((child, index) => (
            <React.Fragment key={child.label}>
              {index > 0 && " / "}
              <ChildMark mark={child} />
            </React.Fragment>
          ))}
          )
        </span>
      )}
      {iconPosition === "after" && icon}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const StyledPanel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue",
    Arial, sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: var(--color-theme-900);
  padding: 12px 16px;
  border: 1px solid var(--color-theme-300);
  border-radius: 10px;
  background: var(--color-theme-100);

  .ap-row {
    display: flex;
    gap: 10px;
    padding: 6px 0;
    align-items: baseline;
    flex-wrap: wrap;
  }

  .ap-row:first-of-type {
    padding-top: 0;
  }

  .ap-row:last-of-type {
    padding-bottom: 0;
  }

  .ap-row + .ap-row {
    border-top: 1px dashed var(--color-theme-300);
  }

  .ap-label {
    flex: 0 0 84px;
    color: var(--color-theme-700);
    font-weight: 600;
    font-size: 13px;
  }

  .ap-vals {
    flex: 1 1 240px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    align-items: center;
  }

  .ap-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  }

  .ap-sub {
    color: var(--color-theme-600);
  }

  .ap-tbd {
    color: var(--color-theme-600);
    font-style: italic;
  }

  .ap-sep {
    color: var(--color-theme-400);
  }

  .ap-excluded {
    text-decoration: line-through;
    color: var(--color-theme-500);
  }

  .ap-ic {
    flex: none;
    vertical-align: -2px;
  }

  .ap-yes {
    color: var(--color-green-olive-drab);
  }

  .ap-clock {
    color: var(--color-theme-600);
  }
`;

export const FeatureAvailability = ({
  rows,
  className,
}: FeatureAvailabilityProps): ReactElement => (
  <StyledPanel className={className} aria-label="Feature availability">
    {rows.map((row) => {
      const iconPosition = row.iconPosition ?? "after";

      return (
        <div className="ap-row" key={row.label}>
          <span className="ap-label">{row.label}</span>
          <span className="ap-vals">
            {row.items.map((item, index) => (
              <React.Fragment key={item.label}>
                {index > 0 && row.separated && (
                  <span className="ap-sep">·</span>
                )}
                <Mark mark={item} iconPosition={iconPosition} />
              </React.Fragment>
            ))}
          </span>
        </div>
      );
    })}
  </StyledPanel>
);

/* ------------------------------------------------------------------ */
/* Illustrative defaults                                               */
/* ------------------------------------------------------------------ */

/**
 * A realistic GA feature: shipped on Cloud, self-hosted still to be scheduled,
 * on every plan, for internal roles but not for clients. Exercises all three
 * marks (check / clock / strikethrough) out of the box.
 */
export const DEFAULT_FEATURE_AVAILABILITY: FeatureAvailabilityProps = {
  rows: [
    {
      label: "Availability",
      iconPosition: "before",
      separated: true,
      items: [
        {
          label: "Cloud",
          status: "available",
          note: "(stable, since Jun 17 2026)",
        },
        {
          label: "Self-Hosted",
          status: "pending",
          pending: "to be determined",
        },
      ],
    },
    {
      label: "Plans",
      items: [
        { label: "S–Mega", status: "available" },
        { label: "Plus", status: "available" },
        { label: "Pro", status: "available" },
        { label: "Pro+Get-Paid", status: "available" },
      ],
    },
    {
      label: "Roles",
      items: [
        { label: "Owner", status: "available" },
        { label: "Member", status: "available" },
        {
          label: "Member+",
          status: "available",
          children: [
            { label: "PM", status: "available" },
            { label: "FM", status: "available" },
          ],
        },
        { label: "Client", status: "excluded" },
        { label: "Client+", status: "excluded" },
      ],
    },
  ],
};
